<template>
    <div>
        <div class="flex justify-between mb-5 border-b border-gray-200 pb-2">
            <div class="flex items-center">
                <h1 class="module-header">Contacts</h1>
                <div class="ml-3 flex items-center text-gray-600">
                    <div class="text-2xl">/</div>
                    <div class="ml-2 pt-1 text-xl">{{ this.contacts.filtered.length }} of {{ this.contacts.all.length }}</div>
                </div>
            </div>
            <div class="flex items-center">
                <filters
                    v-if="showFilters"
                    data-type="contacts"
                    sort-by="name"
                    :showClientFilter="false"
                    :selected-view-option="layout"
                    @view-change="layout = $event">
                </filters>
                <button 
                    v-if="showCreateNew && canCreateContact" 
                    @click="$modal.show('new-contact')" 
                    class="ml-3 flex items-center btn btn-small btn-indigo">
                    <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                    <span class="ml-2">New Contact</span>
                </button>
            </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6" v-if="layout == 'grid'">
            <div v-for='contact in filteredContacts' :key="contact.id" class="shadow">
                <div class="box h-full">
                    <div class="flex w-full h-full">
                        <div :class="contact.avatar ? 'w-64' : 'w-48'">
                            <img v-if="contact.avatar" class="w-full h-full" :src="contact.avatar.view_url">
                            <img v-if="! contact.avatar" class="w-full h-full" src="/img/temp/blank-avatar.jpg">
                        </div>
                        <div class="flex flex-1 flex-col p-4">
                            <div class="flex-1">
                                <div class="mb-6">
                                    <p class="text-xl text-blue-500 font-bold">{{ contact.name }}</p>
                                    <p class="text-sm">{{ contact.type }}</p>
                                </div>
                                <div>
                                    <p class="text-xl">{{ contact.company?.name || '' }}</p>
                                    <p class="text-base">{{ contact.title }}</p>
                                </div>
                            </div>
                            <div class="mt-4 leading-normal text-base">
                                <div v-if="contact.cell" class="flex items-center mb-1">
                                    <inline-svg name="phone"></inline-svg>
                                    <span>{{ contact.cell }}</span>
                                </div>
                                <div v-if="contact.phone && ! contact.cell" class="flex items-center mb-1">
                                    <inline-svg name="phone"></inline-svg>
                                    <span>{{ contact.phone }}</span>
                                    <span v-if="contact.extension" class="ml-2">x{{ contact.extension }}</span>
                                </div>
                                <div v-if="contact.email" class="flex items-center">
                                    <inline-svg name="email"></inline-svg>
                                    <span><a :href="'mailto:' + contact.email">{{ contact.email }}</a></span>
                                </div>                        
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="layout == 'table' && filteredContacts.length">
            <table class="w-full">
                <thead class="cursor-pointer">
                    <tr>
                        <th></th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Cell</th>
                        <th>Office</th>
                        <th>Email</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="contact in filteredContacts" :key="contact.id">
                        <td>
                            <div class="flex justify-center px-2">
                                <img v-if="contact.avatar" class="w-8 h-8 rounded-full shadow-md" :src="contact.avatar.view_url">
                                <inline-svg v-if="! contact.avatar" name="user"></inline-svg>
                            </div>
                        </td>
                        <td class="leading-normal">
                            <p class="text-base text-blue-600 font-medium">{{ contact.name }}</p>
                            <p class="text-sm">{{ contact.type }}</p>
                        </td>
                        <td class="leading-normal">
                            <p class="text-lg">{{ contact.company?.name || '' }}</p>
                            <p class="text-base">{{ contact.title }}</p>
                        </td>
                        <td>{{ contact.cell }}</td>
                        <td>{{ contact.phone }} <span v-if="contact.extension">x{{ contact.extension }}</span></td>
                        <td>
                            <a class="text-sm font-medium" :href="'mailto:' + contact.email">{{ contact.email }}</a>
                        </td>
                        <td class="flex items-center justify-center space-x-2">
                            <button @click="showContactEdit(contact)" type="button" class="btn btn-action">Edit</button>
                            <button @click="confirmDeleteContact(contact.id)" type="button" class="btn btn-action">Delete</button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="! contacts.filtered.length">
            <p class="none-found">No Contacts Found</p>
        </div>

        <modal name="new-contact" width="60%" height="auto" click-to-close draggable=".modal-header">
            <new-contact-modal :attach-to="attachTo" :attach-to-id="attachToId"></new-contact-modal>
        </modal>

        <modal name="edit-contact" width="60%" height="auto" click-to-close draggable=".modal-header">
            <edit-contact-modal></edit-contact-modal>
        </modal>
    </div>
</template>

<script>   
    import { mapState, mapActions } from 'vuex';

    export default {
        name: 'ReoptContacts',

        props: {
            contactData: {
                type: [Array, Object],
                required: true
            },

            attachTo: {
                type: String,
                required: true
            },

            attachToId: {
                type: Number,
                required: true
            },

            showFilters: {
              type: Boolean,
              default: true
            },

            showCreateNew: {
              type: Boolean,
              default: true
            },

            view: {
                type: String,
                default: 'grid'
            }
        },

        data() {
            return {
                layout: this.view
            }
        },

        mounted() {
            if (localStorage.getItem('reopt_contacts_layout')) {
                this.layout = localStorage.getItem('reopt_contacts_layout');
            }

            this.contacts.all = this.contactData;
            this.contacts.filtered = this.contactData;

            this.$emit('filterRequest');
        },

        computed: {
            ...mapState(['sites']),
            ...mapState(['contacts']),
            ...mapActions(['deleteContact']),

            canCreateContact() {
                const route = window.location.pathname;
                if (route.includes('/sites/')) {
                    return this.$gate.can('create', 'site');
                } else if (route.includes('/projects/')) {
                    return this.$gate.can('create', 'project');
                }
                return false;
            },

            filteredContacts() {
                return this.contacts.filtered;
            }
        },

        methods: {
            showContactEdit(contact) {
                this.contacts.selectedContact = contact;

                this.$modal.show('edit-contact');
            },

            confirmDeleteContact(contactId) {
                this.$swal({
                    title: 'Delete this contact?',
                    text: "ALL data will be lost",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.$store.dispatch('deleteContact', {
                            attachedTo: this.attachTo,
                            attachedToId: this.attachToId,
                            contactId: contactId
                        }).then(() => {
                            this.$notify({
                                type: 'success',
                                title: 'Success',
                                text: 'Contact Deleted'
                            });
                        });
                    }
                });
            },
        }
    }
</script>